import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Where each person left the cards over the camera picture (`observers-page-motion`, FR9–FR11).
 *
 * One `localStorage` entry per signed-in person, the same on every twin and camera (the spec's
 * ruling 2, 2026-10-04). A card with no entry is at home; only a moved card has one.
 * Positions are a layout preference, never work: they are not in the plan, the draft or any server,
 * and Ctrl+Z never moves a card.
 *
 * A position is stored as a FRACTION of the card's free travel inside the cards layer, not as pixels,
 * so it survives the window, the step list or a banner resizing the picture: `0` is the left (top)
 * limit, `1` the right (bottom) limit, each `inset` in from the layer's edge.
 *
 * Every read and write is inside try/catch and validated, after `Table`'s stored column widths
 * (`packages/design-system/src/Tables/Table.tsx`): a layout preference must never break a render.
 */

/**
 * `planCard` is Floor Plan's and Camera Feeds' shared plan card; `positionTwin` is Position twin's
 * panel, kept apart from it (`onboarding-persistent-scene-position-twin`, R3.2).
 */
export type CardId = 'entity' | 'plan' | 'tracking' | 'dock' | 'planCard' | 'positionTwin';
export const CARD_IDS: readonly CardId[] = [
  'entity',
  'plan',
  'tracking',
  'dock',
  'planCard',
  'positionTwin',
];

export interface CardFraction {
  fx: number;
  fy: number;
}

/**
 * A resizable card's size in CSS pixels (`observers-corner-plan-resize`, FR18), stored as `w` and `h`
 * next to `fx` and `fy` in the same entry. Only the corner plan has one.
 */
export interface CardSize {
  w: number;
  h: number;
}

/** The corner plan's size until the person resizes it, and its smallest (FR13). */
export const PLAN_DEFAULT_SIZE: CardSize = { w: 220, h: 146 };

export const cardPositionsKey = (sub: string): string => `tl.observers.cardPositions.${sub}`;

const VERSION = 1;

const isCardId = (id: string): id is CardId => (CARD_IDS as readonly string[]).includes(id);

const isFraction = (v: unknown): v is CardFraction => {
  if (!v || typeof v !== 'object') return false;
  const { fx, fy } = v as Record<string, unknown>;
  const ok = (n: unknown) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1;
  return ok(fx) && ok(fy);
};

const isSize = (v: Record<string, unknown>): v is Record<string, unknown> & CardSize => {
  const ok = (n: unknown) => typeof n === 'number' && Number.isFinite(n) && n >= 1;
  return ok(v.w) && ok(v.h);
};

export interface CardPositionsRead {
  /** The valid entries, by card. */
  positions: Partial<Record<CardId, CardFraction>>;
  /**
   * Every stored card entry exactly as read — unknown ids and unknown fields included — so a write
   * changes only the one card it is about.
   */
  entries: Record<string, unknown>;
  /** Known card ids whose entry did not validate; each of those cards is at home. */
  dropped: CardId[];
  /** The valid stored sizes, by card — only for a card whose position is valid too. */
  sizes: Partial<Record<CardId, CardSize>>;
  /** Cards whose position is valid but whose stored size is not: kept in place, at default size. */
  droppedSizes: CardId[];
  /** Why the whole entry could not be read (storage threw, not JSON, wrong version), else null. */
  error: unknown;
  /**
   * The entry is a format this build does not know (another version). It is never written over:
   * a newer build's layout survives an older build in the same browser (a rollback, two tabs).
   */
  foreign: boolean;
}

const EMPTY_READ: CardPositionsRead = {
  positions: {},
  entries: {},
  dropped: [],
  sizes: {},
  droppedSizes: [],
  error: null,
  foreign: false,
};

export const readCardPositions = (sub: string): CardPositionsRead => {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(cardPositionsKey(sub));
  } catch (error) {
    return { ...EMPTY_READ, error };
  }
  if (raw === null) return EMPTY_READ;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return { ...EMPTY_READ, error };
  }
  const entry = parsed as { v?: unknown; cards?: unknown } | null;
  if (!entry || typeof entry !== 'object' || !entry.cards || typeof entry.cards !== 'object') {
    return { ...EMPTY_READ, error: new Error('not a card-positions entry') };
  }
  if (entry.v !== VERSION) {
    return {
      ...EMPTY_READ,
      error: new Error(`a v${String(entry.v)} card-positions entry, not v${VERSION}`),
      foreign: true,
    };
  }
  const entries = { ...(entry.cards as Record<string, unknown>) };
  const positions: Partial<Record<CardId, CardFraction>> = {};
  const dropped: CardId[] = [];
  const sizes: Partial<Record<CardId, CardSize>> = {};
  const droppedSizes: CardId[] = [];
  for (const [id, value] of Object.entries(entries)) {
    if (!isCardId(id)) continue;
    if (!isFraction(value)) {
      dropped.push(id);
      continue;
    }
    positions[id] = { fx: value.fx, fy: value.fy };
    // A size is optional; a malformed one is ignored and the card drawn at its default (FR21).
    const fields: Record<string, unknown> = { ...value };
    if (fields.w === undefined && fields.h === undefined) continue;
    if (isSize(fields)) sizes[id] = { w: fields.w, h: fields.h };
    else droppedSizes.push(id);
  }
  return { positions, entries, dropped, sizes, droppedSizes, error: null, foreign: false };
};

/**
 * Set one card's entry, or delete it with `null`, keeping every other entry as it was stored —
 * unknown ids and unknown fields included. The key is removed when no card is left. An entry in a
 * format this build does not know is never written over. Returns the error when nothing was
 * written, else null.
 *
 * With `size`, the card's `w` and `h` are written too; without it, whatever size the entry already
 * carries is kept, so moving a resized card never forgets its size (FR18).
 */
export const writeCardPosition = (
  sub: string,
  id: CardId,
  fraction: CardFraction | null,
  size?: CardSize,
): unknown => {
  const read = readCardPositions(sub);
  if (read.foreign) return read.error;
  const cards: Record<string, unknown> = { ...read.entries };
  const had = cards[id];
  const kept: Record<string, unknown> =
    had && typeof had === 'object' ? { ...(had as Record<string, unknown>) } : {};
  // A malformed stored size is not carried forward by a move: it would be read, dropped and warned
  // about again on every load until the next resize (FR21).
  if (!size && (kept.w !== undefined || kept.h !== undefined) && !isSize(kept)) {
    delete kept.w;
    delete kept.h;
  }
  if (fraction)
    cards[id] = {
      ...kept,
      fx: fraction.fx,
      fy: fraction.fy,
      ...(size ? { w: size.w, h: size.h } : {}),
    };
  else delete cards[id];
  try {
    if (Object.keys(cards).length === 0) window.localStorage.removeItem(cardPositionsKey(sub));
    else window.localStorage.setItem(cardPositionsKey(sub), JSON.stringify({ v: VERSION, cards }));
    return null;
  } catch (error) {
    return error;
  }
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/**
 * `inset` is the clearance at the start of the axis (left or top); `insetEnd`, at its end (right or
 * bottom), defaults to the same. A host gives them apart where a card's home sits clear of a bar
 * (`MovableCard`'s `homeInset`, `onboarding-persistent-scene-camera-feeds-glass` R6.1).
 *
 * A card's `left` (or `top`) as a fraction of its free travel. Where the card has no travel on this
 * axis (it is as big as the layer allows, or bigger), the fraction cannot be measured, and
 * `fallback` — the fraction already stored — is kept.
 */
export const toFraction = (
  px: number,
  layer: number,
  card: number,
  inset: number,
  fallback: number,
  insetEnd: number = inset,
): number => {
  const travel = layer - inset - insetEnd - card;
  if (travel <= 0) return fallback;
  return clamp01((px - inset) / travel);
};

/**
 * The `left` (or `top`) a stored fraction means for this layer and card size. A card with no travel
 * on this axis sits at its HOME edge (`homeEdge`: 0 for left or top, 1 for right or bottom), so a
 * too-big dock stays at the bottom right rather than jumping to the top left (FR9).
 */
export const fromFraction = (
  f: number,
  layer: number,
  card: number,
  inset: number,
  homeEdge: number,
  insetEnd: number = inset,
): number => {
  const travel = layer - inset - insetEnd - card;
  if (travel <= 0) return homeEdge >= 1 ? layer - insetEnd - card : inset;
  return inset + clamp01(f) * travel;
};

export interface CardPositions {
  get: (id: CardId) => CardFraction | undefined;
  /** The card's stored size, if it has been resized (`observers-corner-plan-resize`). */
  getSize: (id: CardId) => CardSize | undefined;
  /** Move the card. A stored size is kept. */
  set: (id: CardId, fraction: CardFraction) => void;
  /** Place and size the card in one write — the end of a resize (FR16). */
  setPlacement: (id: CardId, fraction: CardFraction, size: CardSize) => void;
  /** Send the card home at its default size: its whole entry goes. */
  clear: (id: CardId) => void;
}

/**
 * The signed-in person's card positions, read once on mount and again if the person changes, and
 * written through on every change. With no `sub` — or storage that refuses — positions live only
 * in memory until the step unmounts. Each kind of failure is logged once per mount, never per key
 * press; nothing on screen claims a layout was lost (*Failure States*).
 */
export const useCardPositions = (sub: string | undefined): CardPositions => {
  const [state, setState] = useState(() => ({
    sub,
    read: sub ? readCardPositions(sub) : EMPTY_READ,
  }));
  // The person changed (or `useAuth0` only now has them): re-read before this render commits.
  let current = state;
  if (state.sub !== sub) {
    current = { sub, read: sub ? readCardPositions(sub) : EMPTY_READ };
    setState(current);
  }

  const { read } = current;
  // Keyed on the read's own error and dropped list, which a later `set`/`clear` carries over by
  // reference — so this fires once per actual read, never once per move.
  const { error: readError, dropped, droppedSizes } = read;
  useEffect(() => {
    if (readError) console.warn('[cardPositions] unreadable, using homes', readError);
    if (dropped.length)
      console.warn('[cardPositions] dropped malformed entries, those cards are home', dropped);
    // A malformed size keeps its card's place but not its size (FR21).
    if (droppedSizes.length)
      console.warn(
        '[cardPositions] dropped malformed sizes, those cards are at their default size',
        droppedSizes,
      );
  }, [readError, dropped, droppedSizes]);

  const saveWarned = useRef(false);
  const save = useCallback(
    (id: CardId, fraction: CardFraction | null, size?: CardSize) => {
      if (!sub) return;
      const error = writeCardPosition(sub, id, fraction, size);
      if (error && !saveWarned.current) {
        saveWarned.current = true;
        console.warn('[cardPositions] not saved', error);
      }
    },
    [sub],
  );

  const set = useCallback(
    (id: CardId, fraction: CardFraction) => {
      setState(s => ({
        ...s,
        read: { ...s.read, positions: { ...s.read.positions, [id]: fraction } },
      }));
      save(id, fraction);
    },
    [save],
  );

  const setPlacement = useCallback(
    (id: CardId, fraction: CardFraction, size: CardSize) => {
      setState(s => ({
        ...s,
        read: {
          ...s.read,
          positions: { ...s.read.positions, [id]: fraction },
          sizes: { ...s.read.sizes, [id]: size },
        },
      }));
      save(id, fraction, size);
    },
    [save],
  );

  const clear = useCallback(
    (id: CardId) => {
      setState(s => {
        const positions = { ...s.read.positions };
        delete positions[id];
        const sizes = { ...s.read.sizes };
        delete sizes[id];
        return { ...s, read: { ...s.read, positions, sizes } };
      });
      save(id, null);
    },
    [save],
  );

  const { positions, sizes } = read;
  return useMemo(
    () => ({
      get: (id: CardId) => positions[id],
      getSize: (id: CardId) => sizes[id],
      set,
      setPlacement,
      clear,
    }),
    [positions, sizes, set, setPlacement, clear],
  );
};
